import { inject, Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { ShopSettings } from '../generic/classes/shop.settings';
import { ConfigurationHelper } from '../generic/helper/configuration.helper';
import { UserService } from '../generic/service/user.service';
import { PlantscapingService } from '../generic/service/plantscaping.service';

@Injectable()
export class LoggedInMandatoryGuardService {
    protected router = inject(Router);
    private userService = inject(UserService);
    private plantscapingService = inject(PlantscapingService);

    private shopSettings: ShopSettings = ConfigurationHelper.getSettings();

    /**
     * Redirect to the login page if not logged in and the shop requires
     * a login.
     *
     * @param route
     * @param state
     * @returns {boolean}
     */
    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean {
        const plantScapingRequested =
            parseInt(route.queryParams.plantscaping, 10) === 1;

        if (this.userService.isLoggedIn()) {
            // If plantscaping is requested, but does not have plantscaping access.
            if (
                plantScapingRequested &&
                !this.userService.hasPlantscapingAccess()
            ) {
                if (this.isKvkShop()) {
                    // Redirect the user back to the `/products` page without the plantscaping query param.
                    this.router.navigate(['/products'], {
                        queryParams: { plantscaping: 0 },
                    });
                    return false;
                }

                this.redirectToPlantScapingLandingPage();
                return false;
            }

            return true;
        }

        if (this.shopSettings.loginMandatory || plantScapingRequested) {
            this.router.navigate(['/login'], {
                queryParams: { returnUrl: state.url },
            });
            return false;
        }

        return true;
    }

    private isKvkShop() {
        return this.shopSettings?.kvkShop;
    }

    private redirectToPlantScapingLandingPage() {
        this.plantscapingService
            .getPlantscapingUrl(false)
            .then(
                (response: any) =>
                    (window.location.href = `${ConfigurationHelper.getWebsiteUrl(response)}`),
            );
    }
}

export const loggedInMandatoryGuard: CanActivateFn = (
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
): boolean => inject(LoggedInMandatoryGuardService).canActivate(next, state);
